import React, { useEffect, useRef, useState, useImperativeHandle } from 'react';
import PropTypes from 'prop-types';
import { t, Trans } from '@lingui/macro';

import ExclamationCircleF from '@decisiv/iconix/lib/components/ExclamationCircleF';
import ChevronDoubleLeft from '@decisiv/iconix/lib/components/ChevronDoubleLeft';
import isEmpty from 'lodash/isEmpty';

import {
  Button,
  Drawer,
  Flex,
  H1,
  P,
  SearchInput,
} from '@decisiv/ui-components';

import {
  HelpInputMessage,
  HelpInputText,
  Separator,
  SeparatorX,
} from './style';
import { PageTitleWrapper } from './PageTile.styled';
import AccordionFilter from './filters/AccordionFilter';
import SelectedFilters from './filters/SelectedFilters';
import IntegrationsApi from '../../api/integrations';
import localCache from '../../utils/localCache';
import { STORAGE_KEYS } from '../../utils/storageKeys';

const PAGE_SIZE = 6;

export default function HomePageTitle({
  setLoading,
  setIntegrations,
  appendIntegrations,
  setHasMore,
  loadMoreRef,
  refreshSignal = 0,
  exportMode = false,
  onSelectAllShown = () => {},
  shownCount = 0,
}) {
  function hasCachedFilters() {
    return localCache.get(STORAGE_KEYS.FILTER_CACHE) !== null;
  }

  const filterFormDefaults = {
    nameSrm: null,
    category: [],
    audience: 'Both',
    serviceNetwork: [],
    trigger: 'Both',
    events: [],
    actions: [],
    commands: [],
    active: 'Both',
    published: 'Both',
    cache: 'Both',
    circuitBreaker: 'Both',
    autoRetry: 'Both',
    metadata: 'Both',
    metadataKey: '',
    metadataValue: '',
    executionConditions: 'Both',
  };

  const tempFilterFormDefaults = {
    category: [],
    audience: 'Both',
    serviceNetwork: [],
    trigger: 'Both',
    events: [],
    actions: [],
    commands: [],
    active: 'Both',
    published: 'Both',
    cache: 'Both',
    circuitBreaker: 'Both',
    autoRetry: 'Both',
    metadata: 'Both',
    metadataKey: '',
    metadataValue: '',
    executionConditions: 'Both',
  };

  const initialSearch = hasCachedFilters()
    ? localCache.get(STORAGE_KEYS.FILTER_CACHE)
    : {
        nameSrm: null,
        category: [],
        audience: 'Both',
        serviceNetwork: [],
        trigger: 'Both',
        events: [],
        actions: [],
        commands: [],
        active: 'Both',
        published: 'Both',
        cache: 'Both',
        circuitBreaker: 'Both',
        autoRetry: 'Both',
        metadata: 'Both',
        metadataKey: '',
        metadataValue: '',
        executionConditions: 'Both',
      };
  const [searchInput, setSearchInput] = useState('');
  const [tempForm, setTempForm] = useState(initialSearch);
  const [form, setForm] = useState(initialSearch);
  const [showFilterDrawer, setShowFilterDrawer] = useState(false);
  const [filterArray, setFilterArray] = useState([]);
  const pageRef = useRef(1);
  const toggleFilterDrawer = () => setShowFilterDrawer(!showFilterDrawer);

  function formatFilters(filters) {
    const cached = typeof filters === 'string' ? JSON.parse(filters) : filters;
    const filterObj = cached || form;

    const compactFilters = Object.fromEntries(
      Object.entries(filterObj).filter(
        ([_key, value]) =>
          // Keep only the key-value pairs where the value is not 'Both' and not an empty array '[]'
          value !== null &&
          value !== '' &&
          value !== 'Both' &&
          !(Array.isArray(value) && value.length === 0),
      ),
    );

    if (!isEmpty(compactFilters.active)) {
      compactFilters.active = compactFilters.active === t`Active`;
    }
    if (!isEmpty(compactFilters.published)) {
      compactFilters.published = compactFilters.published === t`Published`;
    }
    if (!isEmpty(compactFilters.cache)) {
      compactFilters.cache = compactFilters.cache === t`Cache Enabled`;
    }
    if (!isEmpty(compactFilters.circuitBreaker)) {
      compactFilters.circuitBreaker =
        compactFilters.circuitBreaker === t`Circuit Breaker Enabled`;
    }
    if (!isEmpty(compactFilters.autoRetry)) {
      compactFilters.autoRetry =
        compactFilters.autoRetry === t`Automatic Retry Enabled`;
    }
    // Metadata & Execution Conditions presence use the 'present'/'absent'
    // sentinel (not a boolean) because Ransack silently skips a scope whose
    // value sanitizes to `false`. See the scope notes in the API's Integration
    // model.
    if (!isEmpty(compactFilters.metadata)) {
      const hasMetadata = compactFilters.metadata === t`With Metadata`;
      compactFilters.metadata = hasMetadata ? 'present' : 'absent';
      // "Without Metadata" means an empty metadata object, so any key/value
      // match would contradict it (and return nothing). Drop them.
      if (!hasMetadata) {
        delete compactFilters.metadataKey;
        delete compactFilters.metadataValue;
      }
    }
    if (!isEmpty(compactFilters.executionConditions)) {
      compactFilters.executionConditions =
        compactFilters.executionConditions === t`With Execution Conditions`
          ? 'present'
          : 'absent';
    }

    return compactFilters;
  }

  useImperativeHandle(loadMoreRef, () => ({
    async loadMore() {
      const nextPage = pageRef.current + 1;
      const filterCache = localCache.get(STORAGE_KEYS.FILTER_CACHE);
      const myFilters = formatFilters(filterCache);
      const response = await IntegrationsApi.fetchIntegrations({
        ...myFilters,
        page: nextPage,
        limit: PAGE_SIZE,
      });
      if (response.data && response.data.length > 0) {
        pageRef.current = nextPage;
        const more = response.data.length === PAGE_SIZE;
        appendIntegrations(response.data);
        setHasMore(more);
      } else {
        setHasMore(false);
      }
    },
  }));

  useEffect(() => {
    async function buildFilterArray() {
      let resultArray = form;

      resultArray = Object.keys(form)
        .map((key) => {
          const value = form[key];
          if (key === 'nameSrm') {
            return {};
          }

          if (Array.isArray(value)) {
            // If the value is an array, map each element to an object
            return value.map((element) => ({ field: key, value: element }));
          }
          // If the value is not an array, create a single object
          return { field: key, value };
        })
        .flat()
        .filter((element) => Object.keys(element).length !== 0);

      setFilterArray(
        resultArray?.filter(
          (item) =>
            item.value !== 'Both' && item.value !== null && item.value !== '',
        ),
      );
    }

    async function getIntegrations() {
      const filterCache = localCache.get(STORAGE_KEYS.FILTER_CACHE);
      const myFilters = formatFilters(filterCache);
      setLoading(true);
      pageRef.current = 1;
      const response = await IntegrationsApi.fetchIntegrations({
        ...myFilters,
        page: 1,
        limit: PAGE_SIZE,
      });
      /* istanbul ignore else */
      if (response.data) {
        const more = response.data.length === PAGE_SIZE;
        setIntegrations(response.data);
        setHasMore(more);
      } else {
        setIntegrations([]);
        setHasMore(false);
      }
      setLoading(false);
    }

    getIntegrations();
    buildFilterArray();
    setSearchInput(form.nameSrm ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, refreshSignal]);

  const handleForm = ({ event, selectedId, options, field }) => {
    const newForm = { ...tempForm };

    switch (field) {
      // managing combobox multiple values fields
      case 'category':
      case 'commands':
      case 'events':
      case 'actions':
      case 'serviceNetwork':
        /* istanbul ignore else */
        if (
          !newForm[field]?.includes(selectedId) &&
          !isEmpty(selectedId) &&
          !options.removed
        ) {
          newForm[field] = newForm[field] || [];
          newForm[field].push(selectedId);
        }

        /* istanbul ignore next */
        if (newForm[field].includes(selectedId) && options.removed) {
          const indexToRemove = newForm[field].indexOf(selectedId);
          if (indexToRemove !== -1) {
            newForm[field].splice(indexToRemove, 1);
          }
        }

        break;
      default:
        newForm[field] = event.target.value;
        // "Without Metadata" filters to empty metadata, so a key/value match
        // makes no sense — clear them so no stale/contradictory filter lingers.
        if (field === 'metadata' && event.target.value === 'Without Metadata') {
          newForm.metadataKey = '';
          newForm.metadataValue = '';
        }
        break;
    }

    setTempForm(newForm);
  };

  const closeDrawer = () => {
    setShowFilterDrawer(false);
  };

  const clearFilters = () => {
    localCache.remove(STORAGE_KEYS.FILTER_CACHE);
    setForm(filterFormDefaults);
    setTempForm(filterFormDefaults);
    setSearchInput('');
  };

  const clearTempFilters = () => {
    setTempForm({ ...form, ...tempFilterFormDefaults });
  };

  const applyFilter = () => {
    setForm(tempForm);
    localCache.set(STORAGE_KEYS.FILTER_CACHE, tempForm);
    setShowFilterDrawer(false);
  };

  const onSearchHandler = () => {
    const newForm = { ...form };
    newForm.nameSrm = searchInput;
    localCache.set(STORAGE_KEYS.FILTER_CACHE, newForm);
    setTempForm(newForm);
    setForm(newForm);
  };

  const onChangeSearchInputHandler = (event) => {
    if (event.target.value === '') {
      const newForm = { ...form };
      newForm.nameSrm = '';
      localCache.set(STORAGE_KEYS.FILTER_CACHE, newForm);
      setTempForm(newForm);
      setForm(newForm);
    }

    setSearchInput(event.target.value);
  };

  return (
    <>
      <Drawer
        title="Filters"
        visible={showFilterDrawer}
        onClose={closeDrawer}
        side="right"
        // KDS keys these by `action.id` (Drawer/index.js:98), so each needs one.
        actions={[
          {
            id: 'apply-filters',
            text: t`Apply`,
            onClick: applyFilter,
          },
          {
            id: 'cancel-filters',
            text: t`Cancel`,
            onClick: closeDrawer,
            kind: 'secondary',
          },
          {
            id: 'clear-filters',
            text: t`Clear All`,
            onClick: clearTempFilters,
            variant: 'ghost',
            style: { marginLeft: '7rem' },
          },
        ]}
      >
        <Flex flexDirection="column" style={{ width: '100%' }}>
          <Flex marginTop={0.7}>
            <AccordionFilter handleForm={handleForm} form={tempForm} />
          </Flex>
        </Flex>
      </Drawer>
      <PageTitleWrapper>
        <Flex name="header" flexDirection="column" style={{ width: '100%' }}>
          <Flex
            name="searchBoxRow"
            justifyContent="space-between"
            style={{ padding: '0.65rem 1.25rem' }}
          >
            <Flex name="searchBox">
              <Flex>
                <H1 marginRight={2} marginTop={0.2}>
                  <Trans> Integrations </Trans>
                </H1>
                <Separator />
              </Flex>
              <Flex flexDirection="column">
                <SearchInput
                  value={searchInput}
                  width={32}
                  label={t`searchInput`}
                  name="nameSrmSearch"
                  onChange={onChangeSearchInputHandler}
                  onSearch={onSearchHandler}
                  size="small"
                  searchDisabled={false}
                />
                <HelpInputMessage alignItems="center">
                  <ExclamationCircleF />
                  <HelpInputText shade={1}>
                    <Trans>Search by integration or SRM account</Trans>
                  </HelpInputText>
                </HelpInputMessage>
              </Flex>
            </Flex>
            <Flex name="filterDrawerButtonBox" flexDirection="row">
              {exportMode && shownCount > 0 && (
                <Flex marginRight={1}>
                  <Button
                    text={t`Select All Shown`}
                    kind="secondary"
                    variant="ghost"
                    size="small"
                    onClick={onSelectAllShown}
                  />
                </Flex>
              )}
              {isEmpty(filterArray) ? (
                <Button
                  text={t`Filters`}
                  icon={ChevronDoubleLeft}
                  iconPosition="left"
                  kind="secondary"
                  size="small"
                  variant="ghost"
                  onClick={toggleFilterDrawer}
                />
              ) : (
                <Button
                  text={`${filterArray.length} Filters `}
                  icon={ChevronDoubleLeft}
                  iconPosition="left"
                  size="small"
                  onClick={toggleFilterDrawer}
                />
              )}
            </Flex>
          </Flex>
          <Flex name="separatorRow">
            <SeparatorX />
          </Flex>
          {!isEmpty(filterArray) && (
            <>
              <Flex
                name="selectedFiltersRow"
                style={{ padding: '0.5rem 1.25rem' }}
              >
                <Flex name="advancedFilterLabel">
                  <P> Filters: </P>
                </Flex>
                <SelectedFilters
                  items={filterArray}
                  clearAll={clearFilters}
                  form={form}
                  setForm={setForm}
                  setTempForm={setTempForm}
                />
              </Flex>
              <Flex name="separatorRow">
                <SeparatorX />
              </Flex>
            </>
          )}
        </Flex>
      </PageTitleWrapper>
    </>
  );
}

HomePageTitle.propTypes = {
  setLoading: PropTypes.func.isRequired,
  setIntegrations: PropTypes.func.isRequired,
  appendIntegrations: PropTypes.func.isRequired,
  setHasMore: PropTypes.func.isRequired,
  loadMoreRef: PropTypes.shape({ current: PropTypes.shape({}) }).isRequired,
  refreshSignal: PropTypes.number,
  exportMode: PropTypes.bool,
  onSelectAllShown: PropTypes.func,
  shownCount: PropTypes.number,
};
